<template>
  <v-text-field
    v-bind="$attrs"
    class="theme-input"
    :type="type"
    :value="value"
    v-on="$listeners"
    @input="$emit('input', $event)"
  >
    <template
      v-if="isStepper"
      v-slot:append
    >
      <v-btn
        icon
        small
        class="stepper-btn"
        :disabled="$attrs.disabled"
        @click="increment"
      >
        <v-icon
          small
          color="var(--color-text)"
        >
          mdi-chevron-up
        </v-icon>
      </v-btn>
      <v-btn
        icon
        small
        class="stepper-btn"
        :disabled="$attrs.disabled"
        @click="decrement"
      >
        <v-icon
          small
          color="var(--color-text)"
        >
          mdi-chevron-down
        </v-icon>
      </v-btn>
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: "ThemedTextField",
  inheritAttrs: false,
  props: {
    value: [Number, String],
    step: {
      type: Number,
      default: 1,
    },
    type: {
      type: String,
      default: "text",
    },
  },
  computed: {
    isStepper() {
      return this.type === "number";
    },
  },
  methods: {
    increment() {
      const val = parseFloat(this.value) || 0;
      this.$emit("input", val + this.step);
    },
    decrement() {
      const val = parseFloat(this.value) || 0;
      this.$emit("input", val - this.step);
    },
  },
};
</script>

<style scoped>
.theme-input::v-deep .v-input__slot {
  backdrop-filter: blur(10px);
  border: 1px solid var(--color-border) !important;
  border-radius: var(--radius-md) !important;
  transition: var(--transition-default);
  padding: 12px 16px;
  background-color: var(--color-surface-light) !important;
}

.theme-input::v-deep .v-input__slot:hover {
  background-color: var(--color-surface-light) !important;
  border-color: var(--color-secondary-light) !important;
  border: 1px solid var(--color-secondary-light) !important;
}

.theme-input::v-deep .v-input--is-focused .v-input__slot {
  background-color: var(--color-surface) !important;
  border-color: var(--color-secondary) !important;
}

.theme-input::v-deep .v-label {
  color: var(--color-text-muted) !important;
  font-weight: 500;
  font-size: 14px;
}

.theme-input::v-deep .v-input__append-inner,
.theme-input::v-deep .v-input__prepend-inner {
  margin-top: 0 !important;
  align-self: center;
}

.theme-input::v-deep .v-input__append-inner .v-icon,
.theme-input::v-deep .v-input__prepend-inner .v-icon {
  color: var(--color-text) !important;
}

.theme-input::v-deep .v-input__prepend-inner {
  margin-right: 8px !important;
}

.theme-input::v-deep input {
  color: var(--color-text) !important;
  font-weight: 500;
  font-size: 14px;
}

/* Error state styling */
.theme-input::v-deep .v-input--error .v-input__slot {
  border-color: var(--color-error) !important;
}

.theme-input::v-deep .v-input--error .v-label {
  color: var(--color-error) !important;
}

.theme-input::v-deep .v-messages {
  min-height: 16px;
  padding-top: 2px;
  margin-top: -1.5px;
}

.theme-input::v-deep .v-messages__message {
  color: var(--color-error) !important;
  font-size: 12px;
  line-height: 1;
  font-weight: 500;
}

/* Animation for focus state */
.theme-input::v-deep .v-input__slot {
  animation: inputFocus 0.3s ease-out;
}

@keyframes inputFocus {
  from {
    opacity: 0.8;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Custom scrollbar for input */
.theme-input::v-deep input::-webkit-scrollbar {
  width: 6px;
}

.theme-input::v-deep input::-webkit-scrollbar-track {
  background: var(--color-surface);
  border-radius: var(--radius-sm);
}

.theme-input::v-deep input::-webkit-scrollbar-thumb {
  background: var(--color-text-muted);
  border-radius: var(--radius-sm);
}

.theme-input::v-deep input::-webkit-scrollbar-thumb:hover {
  background: var(--color-text);
}

.theme-input::v-deep .stepper-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  margin-left: 4px;
  color: transparent;
  background-color: transparent;
  transition: background-color 0.2s ease;
}

.theme-input::v-deep .stepper-btn:hover {
  background-color: var(--color-secondary-light);
  color: var(--color-text);
}

/* Remove number input spinners – Chrome, Edge, Safari */
.theme-input::v-deep input[type="number"]::-webkit-inner-spin-button,
.theme-input::v-deep input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove number input spinners – Firefox */
.theme-input::v-deep input[type="number"] {
  -moz-appearance: textfield;
}
</style>
